import { useMemo } from 'react'
import type { RefObject } from 'react'
import { useReactFlow, useStore } from '@xyflow/react'
import { hasIndication } from './guidelines'
import type { GuidelineIndication } from './guidelines'

interface Props {
  /**
   * The lines the box being carried would settle onto, in chart coordinates,
   * or null when nothing is being dragged.
   */
  lines: GuidelineIndication | null
  /** The canvas the lines are drawn over and clipped to. */
  paneRef: RefObject<HTMLDivElement>
}

/**
 * Picks out the guideline a moving box is about to land on, while it is still
 * moving.
 *
 * Without this the snap only ever appears after the mouse is released, which
 * reads as the chart correcting the placement rather than as the placement
 * being helped. The lines come from the same threshold the release uses, so
 * what is shown here is exactly what happens on the drop.
 *
 * Like the selection toolbar, the camera is subscribed to HERE rather than in
 * the page: with nothing to draw the subscription returns a constant, so
 * panning and zooming an idle chart re-renders nothing.
 */
export default function GuidelineSnapLayer({ lines, paneRef }: Props) {
  const { flowToScreenPosition } = useReactFlow()
  const active = hasIndication(lines)
  const camera = useStore((state: any) => (
    active ? `${Math.round(state.transform[0])}:${Math.round(state.transform[1])}:${state.transform[2].toFixed(4)}` : ''
  ))

  const drawn = useMemo(() => {
    if (!active || !lines) return null
    const pane = paneRef.current?.getBoundingClientRect()
    if (!pane || typeof flowToScreenPosition !== 'function') return null
    void camera
    // One conversion does both axes: an unused axis rides along on a zero and
    // is thrown away below.
    const point = flowToScreenPosition({ x: lines.x ?? 0, y: lines.y ?? 0 })
    return {
      pane,
      spacing: lines.spacing?.map(s => {
        const a = flowToScreenPosition({ x: s.from, y: s.y })
        const b = flowToScreenPosition({ x: s.to, y: s.y })
        return { x: a.x - pane.left, y: a.y - pane.top, width: b.x - a.x }
      }),
      left: lines.x === null ? null : point.x - pane.left,
      top: lines.y === null ? null : point.y - pane.top,
    }
  }, [active, camera, flowToScreenPosition, lines, paneRef])

  if (!drawn) return null
  const { pane } = drawn
  return (
    <div
      aria-hidden="true"
      data-testid="chart-snap-guides"
      style={{
        position: 'fixed',
        left: pane.left,
        top: pane.top,
        width: pane.width,
        height: pane.height,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {drawn.spacing?.map((s, i) => (
        <div key={i} data-testid="chart-spacing-guide" style={{
          position: 'absolute', left: s.x, top: s.y, width: s.width, height: 7,
          borderLeft: '1px solid #858585', borderRight: '1px solid #858585',
        }}>
          <div style={{ position: 'absolute', top: 3, width: '100%', borderTop: '1px solid #858585' }} />
        </div>
      ))}
      {drawn.left !== null && (
        <div
          data-testid="chart-snap-guide-x"
          data-guideline-axis="x"
          className="chart-snap-guide chart-snap-guide-vertical"
          style={{ left: drawn.left }}
        />
      )}
      {drawn.top !== null && (
        <div
          data-testid="chart-snap-guide-y"
          data-guideline-axis="y"
          className="chart-snap-guide chart-snap-guide-horizontal"
          style={{ top: drawn.top }}
        />
      )}
    </div>
  )
}
